import React from 'react';
import { Redirect, Route, Switch, RouteComponentProps } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

// components
import AboutPage from 'appExplainers/pages/AboutPage';
import FaqPage from 'appExplainers/pages/FaqPage';
import Document from 'documentDisplay/pages/DocumentPage';
import DemoSettings from 'demoSettings/DemoSettingsPage';
import SettingsPage from 'settings/pages/SettingsPage';
import ChatPage from 'eCoach/_pages/ChatPage';
import NotFound from '404/pages/NotFound';
import TeamPage from 'teamProfiles/pages/_index';
import EmployeeProfile from 'teamProfiles/pages/ProfilePage';
import EditDetails from 'teamProfiles/pages/EditDetailsPage';
import TeamLeadInsightsPage from 'teamLeadInsights/pages/TeamLeadInsights';
import DashboardPage from 'dashboard/pages/DashboardPage';
import { EventRoutePage } from 'events/_index';
import FormPage from 'form/pages/FormPage';
import PrioritiesPage from 'priorities/pages/PrioritiesPage';

// hooks
import { useUser } from 'shared/hooks/api/user';
import useCanAccessConversations from 'eCoach/hooks/useCanAccessConversations';

// constants
import routesConfig from 'constants/routesConfig.json';

interface RouteParams {
  id?: string;
}

const POSTHOG_ENABLED = import.meta.env.REACT_APP_POSTHOG_ENABLED === 'true';

function getHomePage(
  canAccessShiftNotes: boolean | undefined,
  canAccessConversations: boolean
): string {
  if (canAccessShiftNotes) return routesConfig.EVENTS_PAGE;
  if (canAccessConversations) return routesConfig.ALL_CONVOS_PAGE;
  return routesConfig.SETTINGS_PAGE;
}

function PageRoutes(): JSX.Element | null {
  const { data: user } = useUser();
  const posthog = usePostHog();

  // When PostHog isn't initialized (local dev, CI, privacy-disabled envs)
  // there are no flags to fetch; default flag-gated routes open instead
  // of leaving them silently unmounted.
  const canAccessShiftNotes = POSTHOG_ENABLED
    ? posthog.isFeatureEnabled('shift_notes')
    : true;
  const canAccessTeamPage =
    !!user?.member?.site &&
    canAccessShiftNotes &&
    (POSTHOG_ENABLED ? posthog.isFeatureEnabled('team_page') : true);
  const canAccessConversations = useCanAccessConversations();
  const homePage = getHomePage(canAccessShiftNotes, canAccessConversations);

  return (
    <Switch>
      <Route
        exact
        path={routesConfig.ROOT_PAGE}
        render={() => <Redirect to={homePage} />}
      />

      <Route
        exact
        path={routesConfig.ONBOARDING_PAGE}
        render={() => (
          <Redirect
            to={
              canAccessConversations ? routesConfig.ALL_CONVOS_PAGE : homePage
            }
          />
        )}
      />

      <Route exact path={routesConfig.ABOUT_PAGE} component={AboutPage} />

      <Route exact path={routesConfig.FAQ_PAGE} component={FaqPage} />

      {canAccessConversations && (
        <Route exact path={routesConfig.ALL_CONVOS_PAGE} component={ChatPage} />
      )}

      {canAccessConversations && (
        <Route
          exact
          path={`${routesConfig.ALL_CONVOS_PAGE}/:tab`}
          component={ChatPage}
        />
      )}

      {canAccessConversations && (
        <Route
          exact
          path={routesConfig.CONVO_PAGE}
          render={() =>
            user.is_demo && !user.is_internal ? (
              <Redirect to={routesConfig.ALL_CONVOS_PAGE} />
            ) : (
              <ChatPage />
            )
          }
        />
      )}

      {canAccessShiftNotes && (
        <Route
          exact
          path={[
            routesConfig.EVENTS_PAGE,
            `${routesConfig.EVENT_PAGE}/:eventId`,
          ]}
          render={({ match }) => (
            <EventRoutePage eventId={match.params.eventId} />
          )}
        />
      )}

      {canAccessShiftNotes && (
        <Route
          exact
          path={routesConfig.PRIORITIES_PAGE}
          component={PrioritiesPage}
        />
      )}

      {canAccessShiftNotes && (
        <Route
          exact
          path={`${routesConfig.INSIGHTS_EVENT_PAGE}/:eventId`}
          render={({ match }) => (
            <Redirect
              to={`${routesConfig.EVENT_PAGE}/${match.params.eventId}`}
            />
          )}
        />
      )}

      {canAccessShiftNotes && (
        <Route
          exact
          path={routesConfig.INSIGHTS_PAGE}
          render={({ location }) => {
            const params = new URLSearchParams(location.search);
            if (params.get('view') !== 'summaries') return <DashboardPage />;
            return <TeamLeadInsightsPage />;
          }}
        />
      )}

      {canAccessShiftNotes && (
        <Route
          exact
          path={`${routesConfig.SHIFT_NOTES_PAGE}/:id/form`}
          component={FormPage}
        />
      )}

      {canAccessConversations && (
        <Route
          exact
          path={`${routesConfig.CONVO_PAGE}/:id?`}
          component={ChatPage}
        />
      )}

      <Route path={`${routesConfig.DOCUMENT_PAGE}/:id/pdf`}>
        {({ match, location }: RouteComponentProps<RouteParams>) => (
          <Redirect
            to={`${routesConfig.DOCUMENT_PAGE}${
              match?.params?.id ? `/${match.params.id}` : ''
            }${location?.search ?? ''}`}
          />
        )}
      </Route>

      <Route
        exact
        path={`${routesConfig.DOCUMENT_PAGE}/:id`}
        component={Document}
      />

      <Route
        exact
        path={routesConfig.DEMO_SETTINGS_PAGE}
        component={DemoSettings}
      />

      <Route exact path={routesConfig.SETTINGS_PAGE} component={SettingsPage} />

      {canAccessTeamPage && [
        <Route
          key="team"
          exact
          path={routesConfig.TEAM_PAGE}
          component={TeamPage}
        />,
        <Route
          key="team-new"
          exact
          path={`${routesConfig.TEAM_PAGE}/new`}
          component={EditDetails}
        />,
        <Route
          key="team-id"
          exact
          path={`${routesConfig.TEAM_PAGE}/:id`}
          component={EmployeeProfile}
        />,
        <Route
          key="team-edit"
          exact
          path={`${routesConfig.TEAM_PAGE}/:id/edit`}
          component={EditDetails}
        />,
      ]}

      {canAccessConversations && (
        <Route path={routesConfig.LEGACY.ALL_CONVOS_PAGE}>
          <Redirect to={routesConfig.ALL_CONVOS_PAGE} />
        </Route>
      )}

      <Route path={routesConfig.LEGACY.INSIGHTS_PAGE}>
        <Redirect to={routesConfig.INSIGHTS_PAGE} />
      </Route>

      {canAccessConversations && [
        <Route
          key="legacy-ecoach-id"
          path={`${routesConfig.LEGACY.ECOACH_PAGE}/:id?`}
        >
          {({ match }: RouteComponentProps<RouteParams>) => (
            <Redirect
              to={`${routesConfig.CONVO_PAGE}${
                match?.params?.id ? `/${match.params.id}` : ''
              }`}
            />
          )}
        </Route>,
        <Route key="legacy-ecoach" path={routesConfig.LEGACY.ECOACH_PAGE}>
          <Redirect to={routesConfig.ALL_CONVOS_PAGE} />
        </Route>,
      ]}

      <Route component={NotFound} exact path="*" />
    </Switch>
  );
}

export default PageRoutes;
